// ONE ACCOUNT'S BOT SETUP (the knowledge base, the stock, the bot). ⚠️ DELIBERATELY FREE OF THE ACCOUNTS MODULE: the account page hosts
// this through its KyckConnect tab, and importing the accounts barrel here would close a barrel-to-barrel cycle (that fails at
// module init, not at compile). The standalone page with its client picker is BotSettingsPage.tsx.

import React, { useEffect, useMemo, useState } from 'react';
import { useGuardedReset } from './guardedReset';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { modeNotice } from '../../lib/leadBot/mode';
import { VERTICAL_LABELS, resolveVertical } from '../../lib/leadBot/verticals';
import { defaultVerticalForIndustry, setupSections } from '../../lib/leadBot/verticalLayout';
import {
  CAP_BOUNDS,
  DEFAULT_RUNAWAY_CAPS,
  capError,
} from '../../lib/leadBot/runaway';
import { DEFAULT_BUSINESS_HOURS } from '../../lib/leadBot/businessHours';
import { BusinessHoursEditor } from './BusinessHoursEditor';
import { AllowlistEditor } from './AllowlistEditor';
import { TesterInviteSection } from './TesterInviteSection';
import { FitnessBotSettings } from './FitnessBotSettings';
import { StockSection } from './StockSection';
import {
  KNOWLEDGE_BASE_SECTIONS,
  type BotBusinessHours,
  type BotKnowledgeBaseSectionKey,
  type BotMode,
  type BotRunawayCaps,
  type BotVertical,
} from '../../types/leadBot';
import {
  createBotConfig,
  getKnowledgeBase,
  listBotConfigs,
  saveKnowledgeBase,
  updateBotConfig,
  type KnowledgeBaseArgs,
} from './api';

// Bot settings — the knowledge base and the bot configuration.
//
// ⚠️ THE KNOWLEDGE BASE IS THE WHOLE PRODUCT, and the page says so. Hard rule 1
// means the bot may state as fact ONLY what appears here, in words: with an
// empty knowledge base it is a polite deflection machine, and with a good one
// it is genuinely useful. That is the difference this form makes, so it leads
// the page rather than hiding behind the config.

// There is no shared `Section` primitive in components/ui (AccountDetail keeps
// its own), so this page composes one from Card rather than inventing a third
// bespoke heading treatment.
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

const EMPTY_KB: KnowledgeBaseArgs = {
  businessName: '',
  about: '',
  addresses: '',
  hours: '',
  brandsStocked: '',
  services: '',
  financePolicy: '',
  tradeInPolicy: '',
  warranty: '',
  viewingProcess: '',
  extraNotes: '',
};

const SECTION_HINTS: Record<BotKnowledgeBaseSectionKey, string> = {
  about: 'What the business does, who it serves, how long it has been going.',
  addresses: 'Every showroom or yard, with suburb and state.',
  hours: 'Opening hours per site. The bot may only quote what is written here.',
  brandsStocked: 'The makes and brands carried.',
  services: 'Sales, servicing, parts, fit-outs — whatever the team actually does.',
  financePolicy:
    'What can be said about finance in general. ⚠️ Specific rates and terms are always a handover, whatever is written here.',
  tradeInPolicy: 'How trade-ins work. Valuations are always a handover.',
  warranty: 'What warranty applies and what it covers.',
  viewingProcess: 'What happens when someone comes in — who they meet, how long it takes.',
  extraNotes: 'Anything else the bot should know. Common questions and their answers work well.',
};

/**
 * ⚠️ An EMPTY box becomes NaN, not 0 — so it fails validation with "needs a
 * number" rather than silently saving a cap of zero, which is the one value
 * that must never mean anything. `Number('')` is 0, which is exactly the trap.
 */
export function toCapNumber(raw: string): number {
  const trimmed = raw.trim();
  return trimmed === '' ? Number.NaN : Number(trimmed);
}

/**
 * ONE ACCOUNT'S BOT SETUP: the knowledge base, the stock and the bot itself. Used by the standalone Bot settings page (after its
 * client picker) AND by the account's KyckConnect -> Setup -> AI SMS step, so the two cannot drift.
 */
export function BotSetup({
  accountId,
  accountName,
  accountIndustry = null,
  isAdmin = false,
}: {
  accountId: string;
  accountName: string;
  /** The account's industry: it picks the default vertical for a NEW bot. */
  accountIndustry?: string | null;
  /** Only a Kyck admin may change an existing bot's vertical. */
  isAdmin?: boolean;
}) {
  const queryClient = useQueryClient();
  const { data: configs = [], isLoading: configsLoading } = useQuery({
    queryKey: ['lead-bot', 'configs'],
    queryFn: listBotConfigs,
  });
  const config = configs.find((c) => c.accountId === accountId) ?? null;
  // A bot that does not exist yet takes its vertical from the picker (defaulted from the industry); an existing one from its config.
  const [chosen, setChosen] = useState<BotVertical | null>(null);
  const vertical: BotVertical = config ? resolveVertical(config) : (chosen ?? defaultVerticalForIndustry(accountIndustry) ?? 'automotive');
  const sections = setupSections(vertical);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['lead-bot'] });
  return (
    <div className="space-y-6">
      {configsLoading ? null : (
        <VerticalPicker config={config} vertical={vertical} onChoose={setChosen} isAdmin={isAdmin} onChanged={refresh} />
      )}
      {sections.knowledgeBase ? <KnowledgeBaseForm accountId={accountId} /> : null}
      {/* ⚠️ Stock sits with the knowledge base and is keyed the same way —
          per ACCOUNT — because it belongs to the business rather than to a
          bot. A client with two bots has one stock list. */}
      {sections.vehicleStock ? (
        <Section title="Vehicle stock">
          <StockSection accountId={accountId} />
        </Section>
      ) : null}
      {configsLoading ? (
        <Spinner />
      ) : (
        <BotConfigForm
          accountId={accountId}
          accountName={accountName}
          config={config}
          vertical={vertical}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

/**
 * THE VERTICAL, AT THE TOP OF THE SETUP. Chosen at creation (defaulted from the account's industry), shown as a label afterwards,
 * and changed only by a Kyck admin after a confirm: the change swaps which sections apply, it deletes nothing.
 */
function VerticalPicker({
  config,
  vertical,
  onChoose,
  isAdmin,
  onChanged,
}: {
  config: Awaited<ReturnType<typeof listBotConfigs>>[number] | null;
  vertical: BotVertical;
  onChoose: (v: BotVertical) => void;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const change = useMutation({
    mutationFn: (next: BotVertical) => updateBotConfig(config!.id, { vertical: next }),
    onSuccess: () => {
      toast.success('Bot type changed.');
      onChanged();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const options = (Object.keys(VERTICAL_LABELS) as BotVertical[]).map((v) => ({ value: v, label: VERTICAL_LABELS[v] }));
  return (
    <div className="rounded-lg border p-4" data-testid="bot-vertical">
      <Label htmlFor="bot-vertical-select">Type of bot</Label>
      {!config ? (
        <div className="max-w-sm">
          <SelectField id="bot-vertical-select" value={vertical} onValueChange={(v) => onChoose(v as BotVertical)} options={options} />
          <p className="mt-1 text-xs text-muted-foreground">Chosen when the bot is created. It decides which setup sections apply.</p>
        </div>
      ) : isAdmin ? (
        <div className="max-w-sm">
          <SelectField
            id="bot-vertical-select"
            value={vertical}
            onValueChange={(v) => {
              const next = v as BotVertical;
              if (next === vertical) return;
              if (window.confirm(`Change this bot to "${VERTICAL_LABELS[next]}"? The sections below change. Nothing already saved is deleted.`)) change.mutate(next);
            }}
            options={options}
            disabled={change.isPending}
          />
        </div>
      ) : (
        <p className="text-sm" data-testid="bot-vertical-label">
          {VERTICAL_LABELS[vertical]} <span className="text-xs text-muted-foreground">(only a Kyck admin can change this)</span>
        </p>
      )}
    </div>
  );
}

function KnowledgeBaseForm({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<KnowledgeBaseArgs>(EMPTY_KB);

  const { data, isLoading } = useQuery({
    queryKey: ['lead-bot', 'kb', accountId],
    queryFn: () => getKnowledgeBase(accountId),
  });

  useEffect(() => {
    if (data) {
      const { businessName, ...rest } = data;
      setForm({
        businessName,
        about: rest.about,
        addresses: rest.addresses,
        hours: rest.hours,
        brandsStocked: rest.brandsStocked,
        services: rest.services,
        financePolicy: rest.financePolicy,
        tradeInPolicy: rest.tradeInPolicy,
        warranty: rest.warranty,
        viewingProcess: rest.viewingProcess,
        extraNotes: rest.extraNotes,
      });
    } else if (data === null) {
      setForm(EMPTY_KB);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => saveKnowledgeBase(accountId, form),
    onSuccess: () => {
      toast.success('Knowledge base saved.');
      void queryClient.invalidateQueries({ queryKey: ['lead-bot', 'kb', accountId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) return <Spinner />;

  const isEmpty = data === null;

  return (
    <Section title="Knowledge base">
      {/* ⚠️ The load-bearing warning on this page. With nothing here the bot
          cannot confirm a single fact — hard rule 1 — so it deflects every
          question, which looks like a broken bot rather than a missing form. */}
      <div
        className={`mb-4 flex gap-2 rounded-md border p-3 text-sm ${
          isEmpty
            ? 'border-amber-300 bg-amber-50 text-amber-900'
            : 'border-slate-200 bg-slate-50 text-slate-700'
        }`}
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          {isEmpty ? (
            <>
              <strong>This client has no knowledge base yet.</strong> The bot may state as fact
              only what is written here, so until this is filled in it will politely deflect
              every question a lead asks.
            </>
          ) : (
            <>
              The bot may state as fact <strong>only</strong> what is written here, word for
              word. Anything missing becomes "I don't have that detail — let me get someone to
              confirm". Licence, towing and weight questions are always handed to a person
              regardless of what this says.
            </>
          )}
        </p>
      </div>

      <div className="space-y-4">
        <div className="max-w-md">
          <Label htmlFor="kb-business-name">Business name</Label>
          <Input
            id="kb-business-name"
            value={form.businessName}
            onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
            placeholder="Camperagent"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            How the bot refers to the business when it introduces itself.
          </p>
        </div>

        {KNOWLEDGE_BASE_SECTIONS.map((section) => (
          <div key={section.key}>
            <Label htmlFor={`kb-${section.key}`}>{section.label}</Label>
            <Textarea
              id={`kb-${section.key}`}
              value={form[section.key]}
              onChange={(e) => setForm((f) => ({ ...f, [section.key]: e.target.value }))}
              rows={3}
            />
            <p className="mt-1 text-xs text-muted-foreground">{SECTION_HINTS[section.key]}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={() => save.mutate()}
          disabled={save.isPending || form.businessName.trim() === ''}
        >
          {save.isPending ? <Spinner className="h-4 w-4" /> : null}
          Save knowledge base
        </Button>
      </div>
    </Section>
  );
}

export function BotConfigForm({
  accountId,
  accountName,
  config,
  vertical,
  onSaved,
}: {
  accountId: string;
  accountName: string;
  config: Awaited<ReturnType<typeof listBotConfigs>>[number] | null;
  /** The vertical a NEW bot is created with. An existing bot keeps its own (changed only from the picker above). */
  vertical?: BotVertical;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [assistantName, setAssistantName] = useState('');
  const [goal, setGoal] = useState('');
  const [tone, setTone] = useState('');
  const [mode, setMode] = useState<BotMode>('draft');
  const [isActive, setIsActive] = useState(true);
  const [businessHours, setBusinessHours] = useState<BotBusinessHours>(DEFAULT_BUSINESS_HOURS);
  const [allowlist, setAllowlist] = useState<string[]>([]);
  const [knowledgeSupplement, setKnowledgeSupplement] = useState('');
  const [forwardingNumber, setForwardingNumber] = useState('');
  const [maxTurns, setMaxTurns] = useState(6);
  // ⚠️ HELD AS STRINGS so an emptied box stays empty while it is being retyped,
  // rather than snapping to 0 — which under the old semantics would have read as
  // "unlimited". The value is validated on the way out, not coerced on the way in.
  const [maxPerConversation, setMaxPerConversation] = useState(
    String(DEFAULT_RUNAWAY_CAPS.maxMessagesPerConversation),
  );
  const [maxPerHour, setMaxPerHour] = useState(String(DEFAULT_RUNAWAY_CAPS.maxOutboundPerHour));
  const [handoffContactName, setHandoffContactName] = useState('');
  const [handoffPhone, setHandoffPhone] = useState('');
  const [handoffEmail, setHandoffEmail] = useState('');

  // ⚠️ Run U: reset from the stored config only when it cannot lose an unsaved edit (the Fitness card's
  // own save refetches this config too). Keys are listed in the same order on both sides.
  const storedValues = {
    name: config?.name ?? `${accountName} lead bot`,
    assistantName: config?.assistantName ?? '',
    goal: config?.goal ?? 'book a call or a visit with the sales team',
    tone: config?.tone ?? '',
    mode: config?.mode ?? 'draft',
    isActive: config?.isActive ?? true,
    businessHours: config?.businessHours ?? DEFAULT_BUSINESS_HOURS,
    allowlist: config?.testPhoneAllowlist ?? [],
    knowledgeSupplement: config?.knowledgeSupplement ?? '',
    forwardingNumber: config?.forwardingNumber ?? '',
    maxTurns: config?.maxTurnsWithoutProgress ?? 6,
    maxPerConversation: String(config?.caps.maxMessagesPerConversation ?? DEFAULT_RUNAWAY_CAPS.maxMessagesPerConversation),
    maxPerHour: String(config?.caps.maxOutboundPerHour ?? DEFAULT_RUNAWAY_CAPS.maxOutboundPerHour),
    handoffContactName: config?.handoffContactName ?? '',
    handoffPhone: config?.handoffPhone ?? '',
    handoffEmail: config?.handoffEmail ?? '',
  };
  const formValues = {
    name, assistantName, goal, tone, mode, isActive, businessHours, allowlist, knowledgeSupplement,
    forwardingNumber, maxTurns, maxPerConversation, maxPerHour, handoffContactName, handoffPhone, handoffEmail,
  };
  const { dirty } = useGuardedReset({
    key: `${config?.id ?? 'new'}|${accountName}`,
    stored: JSON.stringify(storedValues),
    form: JSON.stringify(formValues),
    apply: () => {
      setName(storedValues.name);
      setAssistantName(storedValues.assistantName);
      setGoal(storedValues.goal);
      setTone(storedValues.tone);
      setMode(storedValues.mode);
      setIsActive(storedValues.isActive);
      setBusinessHours(storedValues.businessHours);
      setAllowlist(storedValues.allowlist);
      setKnowledgeSupplement(storedValues.knowledgeSupplement);
      setForwardingNumber(storedValues.forwardingNumber);
      setMaxTurns(storedValues.maxTurns);
      setMaxPerConversation(storedValues.maxPerConversation);
      setMaxPerHour(storedValues.maxPerHour);
      setHandoffContactName(storedValues.handoffContactName);
      setHandoffPhone(storedValues.handoffPhone);
      setHandoffEmail(storedValues.handoffEmail);
    },
  });

  // ⚠️ THE SAME `capError` THE SERVER RUNS, so the form cannot accept a value
  // the API then rejects — and the "no unlimited" rule has one implementation.
  const capsDraft = {
    maxMessagesPerConversation: toCapNumber(maxPerConversation),
    maxOutboundPerHour: toCapNumber(maxPerHour),
  };
  const perConversationError = capError(capsDraft.maxMessagesPerConversation, 'maxMessagesPerConversation');
  const perHourError = capError(capsDraft.maxOutboundPerHour, 'maxOutboundPerHour');
  const capsValid = perConversationError === null && perHourError === null;

  const save = useMutation({
    mutationFn: async () => {
      const args = {
        name: name.trim(),
        mode,
        isActive,
        assistantName: assistantName.trim(),
        goal: goal.trim(),
        tone: tone.trim(),
        businessHours,
        testPhoneAllowlist: allowlist,
        knowledgeSupplement: knowledgeSupplement.trim(),
        forwardingNumber: forwardingNumber.trim() || null,
        caps: capsDraft as BotRunawayCaps,
        maxTurnsWithoutProgress: maxTurns,
        handoffContactName: handoffContactName.trim() || null,
        handoffPhone: handoffPhone.trim() || null,
        handoffEmail: handoffEmail.trim() || null,
      };
      return config
        ? updateBotConfig(config.id, args)
        : createBotConfig({ ...args, accountId, ...(vertical === 'fitness' ? { vertical, playbook: 'new_lead_engagement' as const } : {}) });
    },
    onSuccess: () => {
      toast.success('Bot saved.');
      onSaved();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ⚠️ Reads the LIVE form value, not the saved one, so the warning tracks
  // what the AM is about to save rather than what is already stored.
  const notice = modeNotice(mode, allowlist.length);

  return (
    <Section title="Bot">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="bot-name">Bot name</Label>
            <Input id="bot-name" value={name} onChange={(e) => setName(e.target.value)} />
            <p className="mt-1 text-xs text-muted-foreground">
              Internal label. A client can have several bots.
            </p>
          </div>
          <div>
            <Label htmlFor="bot-assistant-name">Assistant name</Label>
            <Input
              id="bot-assistant-name"
              value={assistantName}
              onChange={(e) => setAssistantName(e.target.value)}
              placeholder="Sam"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              This is the name leads see, and it appears in this bot's labels and history. It always says it is an assistant, never a person.
            </p>
          </div>
        </div>

        <div>
          <Label htmlFor="bot-goal">What a good conversation achieves</Label>
          <Input
            id="bot-goal"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="book a call or a showroom visit with the sales team"
          />
        </div>

        <div>
          <Label htmlFor="bot-tone">Tone</Label>
          <Input
            id="bot-tone"
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            placeholder="friendly, straight-talking, no marketing speak"
          />
          {/* ⚠️ Free text is only safe because the hard rules say outright that
              nothing later overrides them: tone changes HOW it writes, never
              what it may claim. */}
          <p className="mt-1 text-xs text-muted-foreground">
            Changes how it writes, never what it may claim. "Sell it harder" will not make it
            invent a price.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="bot-mode">Mode</Label>
            <SelectField
              id="bot-mode"
              value={mode}
              onValueChange={(v) => setMode(v as BotMode)}
              options={[
                { value: 'draft', label: 'Draft — every reply waits for approval' },
                { value: 'autonomous', label: 'Autonomous — replies without approval' },
              ]}
            />
            {notice && <p className="mt-1 text-xs text-amber-700">{notice}</p>}
          </div>
          <div>
            <Label htmlFor="bot-active">Status</Label>
            <SelectField
              id="bot-active"
              value={isActive ? 'active' : 'paused'}
              onValueChange={(v) => setIsActive(v === 'active')}
              options={[
                { value: 'active', label: 'Active — new leads use this bot' },
                { value: 'paused', label: 'Paused' },
              ]}
            />
          </div>
        </div>

        <AllowlistEditor value={allowlist} onChange={setAllowlist} mode={mode} />

        {/* ⚠️ Only once the bot EXISTS — a link has to point at something, and
            an invite issued against an unsaved form would have no bot to add
            anyone to. */}
        {config && (
          <div className="rounded-md border p-3">
            <p className="text-sm font-medium">Invite a tester</p>
            <div className="mt-2">
              <TesterInviteSection botConfigId={config.id} isDemo={config.isDemo === true} />
            </div>
          </div>
        )}

        <div>
          <Label htmlFor="bot-supplement">Extra notes for this bot</Label>
          <Textarea
            id="bot-supplement"
            value={knowledgeSupplement}
            onChange={(e) => setKnowledgeSupplement(e.target.value)}
            rows={4}
          />
          {/* ⚠️ ADDITIVE, never a replacement — and treated exactly as
              untrusted as the account knowledge base, because it is. */}
          <p className="mt-1 text-xs text-muted-foreground">
            Added on top of the client's knowledge base above, for this bot only. The same rule
            applies: the bot may state as fact only what is written here or there.
          </p>
        </div>

        <div className="max-w-xs">
          <Label htmlFor="bot-forwarding">Forwarding number for handovers</Label>
          <Input
            id="bot-forwarding"
            value={forwardingNumber}
            onChange={(e) => setForwardingNumber(e.target.value)}
            placeholder="0412 345 678"
          />
          {/* ⚠️ A handoff that evaporates because a config field was blank would
              be worse than one nobody was paged about. */}
          <p className="mt-1 text-xs text-muted-foreground">
            Texted when a conversation needs a person. Leave blank and handovers are still
            recorded and visible in the queue — they simply are not paged.
          </p>
        </div>

        <BusinessHoursEditor value={businessHours} onChange={setBusinessHours} />

        {/* ⚠️ THE CIRCUIT BREAKER, and the copy says what it is FOR — whoever
            raises these should understand what they are turning down. */}
        <div>
          <p className="text-sm font-medium">Runaway limits</p>
          <p className="mb-2 text-xs text-muted-foreground">
            The bot replies on its own. These are the hard stops if something loops — an
            auto-responder on the other end, or a conversation that will not end. Every reply is
            a real SMS to a real phone, so these are what stand between a bad night and a phone
            bill. There is no "unlimited": set a high number if you need room.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="bot-cap-conversation">Replies per conversation</Label>
              <Input
                id="bot-cap-conversation"
                type="number"
                min={CAP_BOUNDS.maxMessagesPerConversation.min}
                max={CAP_BOUNDS.maxMessagesPerConversation.max}
                value={maxPerConversation}
                onChange={(e) => setMaxPerConversation(e.target.value)}
                aria-invalid={perConversationError !== null}
              />
              {perConversationError ? (
                <p className="mt-1 text-xs text-rose-600">{perConversationError}</p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  After this many replies to one lead the bot stops and hands over.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="bot-cap-hour">Replies per hour</Label>
              <Input
                id="bot-cap-hour"
                type="number"
                min={CAP_BOUNDS.maxOutboundPerHour.min}
                max={CAP_BOUNDS.maxOutboundPerHour.max}
                value={maxPerHour}
                onChange={(e) => setMaxPerHour(e.target.value)}
                aria-invalid={perHourError !== null}
              />
              {perHourError ? (
                <p className="mt-1 text-xs text-rose-600">{perHourError}</p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  Across every conversation this bot is having, in any rolling hour.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-xs">
          <Label htmlFor="bot-max-turns">Replies before handing over a stuck conversation</Label>
          <Input
            id="bot-max-turns"
            type="number"
            min={0}
            max={30}
            value={maxTurns}
            onChange={(e) => setMaxTurns(Number(e.target.value))}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            A conversation going in circles is handed to a person. 0 turns this off.
          </p>
        </div>

        <div>
          <p className="text-sm font-medium">Who a handover goes to</p>
          <p className="mb-2 text-xs text-muted-foreground">
            Recorded now and shown on the conversation. Notifying them automatically is a later
            phase — for now the queue is where a handover is picked up.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              value={handoffContactName}
              onChange={(e) => setHandoffContactName(e.target.value)}
              placeholder="Name"
              aria-label="Handover contact name"
            />
            <Input
              value={handoffPhone}
              onChange={(e) => setHandoffPhone(e.target.value)}
              placeholder="Phone"
              aria-label="Handover contact phone"
            />
            <Input
              value={handoffEmail}
              onChange={(e) => setHandoffEmail(e.target.value)}
              placeholder="Email"
              aria-label="Handover contact email"
            />
          </div>
        </div>

        {config && (
          <p className="text-xs text-muted-foreground">
            Business hours are read in <Badge variant="neutral">{config.businessHours.timezone}</Badge>
          </p>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        {dirty && config ? <span className="mr-3 self-center text-xs text-amber-700">Unsaved changes</span> : null}
        <Button
          onClick={() => save.mutate()}
          disabled={
            save.isPending || name.trim() === '' || assistantName.trim() === '' || !capsValid
          }
        >
          {save.isPending ? <Spinner className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {config ? 'Save bot settings' : 'Create bot'}
        </Button>
      </div>

      {/* Fitness vertical, follow-ups and the conference demo: its own card with its own save. */}
      {config ? <FitnessBotSettings config={config} onSaved={onSaved} /> : null}
    </Section>
  );
}
